/**
 * VolumeLineChartCard Component
 *
 * Metric card displaying Volume of Feedback as a line chart over 24 hours.
 * Shows hourly volume counts with a dot for each hour to help identify patterns.
 *
 * Atomic level: Molecule
 * Composes: DeltaIndicator, Card (shadcn)
 * Used by: MetricStrip
 */

import React, { useMemo } from 'react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  ResponsiveContainer,
  CartesianGrid,
} from 'recharts';
import { Card } from '../../../ui/card';
import { ChartContainer, ChartTooltip, ChartTooltipContent } from '../../../ui/chart';
import { DeltaIndicator } from './DeltaIndicator';
import type { VolumeLineChartCardProps } from '../types/today.types';

// Chart configuration
const CHART_CONFIG = {
  HEIGHT: 140,
  LINE_COLOR: 'hsl(var(--primary))',
};

const chartConfig = {
  count: {
    label: 'Records',
    color: CHART_CONFIG.LINE_COLOR,
  },
};

/**
 * Format hour for X-axis labels
 */
function formatHour(isoString: string): string {
  const date = new Date(isoString);
  const hours = date.getHours();
  if (hours === 0) return '12am';
  if (hours === 12) return '12pm';
  if (hours < 12) return `${hours}am`;
  return `${hours - 12}pm`;
}

/**
 * Volume of Feedback metric card with line chart.
 */
export function VolumeLineChartCard({
  value,
  delta,
  trend: _trend,
  hourlyData,
  noData = false,
}: VolumeLineChartCardProps): React.ReactElement {
  // Transform data for the chart
  const chartData = useMemo(() => {
    if (!hourlyData || hourlyData.length === 0) return [];
    return hourlyData.map((point) => ({
      hour: point.hour,
      count: point.count,
      label: formatHour(point.hour),
    }));
  }, [hourlyData]);

  // Get tick values for X-axis (every 4 hours)
  const xAxisTicks = useMemo(() => {
    if (chartData.length === 0) return [];
    const tickIndices = [0, 4, 8, 12, 16, 20];
    return tickIndices
      .filter((i) => i < chartData.length)
      .map((i) => chartData[i].hour);
  }, [chartData]);

  return (
    <Card className="gap-0 overflow-hidden rounded-2xl border-border/70 bg-card/90 p-0 shadow-[var(--shadow-card)]" padding="sm">
      <div className="space-y-4 p-5">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h3 className="font-jakarta text-[15px] font-bold tracking-[-0.02em] text-foreground">
              Volume of Feedback
            </h3>
            <p className="mt-1 text-[12px] text-muted-foreground">Hourly activity over the last 24 hours</p>
          </div>
          <div className="text-right">
            <div className="font-jakarta text-3xl font-bold tracking-[-0.04em] text-foreground">
              {noData ? 'N/A' : value.toLocaleString()}
            </div>
            <DeltaIndicator delta={delta} showValue={true} size="sm" />
          </div>
        </div>

        <div style={{ height: CHART_CONFIG.HEIGHT }}>
          {noData ? (
            <div className="flex h-full items-center justify-center rounded-2xl border border-border/70 bg-muted/30">
              <span className="text-[13px] text-muted-foreground">No data available</span>
            </div>
          ) : (
            <ChartContainer config={chartConfig} className="h-full rounded-xl border border-border/70 bg-background/70 p-3">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border) / 0.7)" />
                  <XAxis
                    dataKey="hour"
                    stroke="hsl(var(--muted-foreground))"
                    fontSize={10}
                    axisLine={false}
                    tickLine={false}
                    ticks={xAxisTicks}
                    tickFormatter={formatHour}
                  />
                  <YAxis
                    stroke="hsl(var(--muted-foreground))"
                    fontSize={10}
                    axisLine={false}
                    tickLine={false}
                    width={30}
                    allowDecimals={false}
                  />
                  <ChartTooltip content={<ChartTooltipContent />} labelFormatter={(label) => formatHour(label as string)} />
                  <Line
                    type="monotone"
                    dataKey="count"
                    stroke={CHART_CONFIG.LINE_COLOR}
                    strokeWidth={3}
                    dot={{ fill: CHART_CONFIG.LINE_COLOR, strokeWidth: 0, r: 4 }}
                    activeDot={{ r: 6, fill: CHART_CONFIG.LINE_COLOR }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </ChartContainer>
          )}
        </div>
      </div>
    </Card>
  );
}
